import React, { useEffect, useState } from "react"
import { Form, Table } from "react-bootstrap"
import { useDispatch } from "react-redux"

import TrashButton from "@/components/buttons/TrashButton"

import { useSliceSelector, useSliceActions } from "@/helpers/SliceProvider"

import RegexSearchInput from "@/components/RegexSearchInput"
import useItemContainer from "@/helpers/useItemContainer"
import { uitest } from "@/helpers/uitest"
import { ChatButton } from "@/components/chat"
import { getForm, getPorts } from "../selectors"

const CurrentPortsTable = ({ onRemove, ports, readOnly }): JSX.Element => (
  <div {...uitest("current_ports")}>
    <h3 className="h6">Current Ports</h3>
    <Table className="mb-3">
      <thead>
        <tr>
          <th> Port </th>
          <th> Country </th>
          <th> Common Region </th>
          <th> CL Region </th>
          {!readOnly && <th> </th>}
        </tr>
      </thead>
      <tbody>
        {ports.length === 0 && (
          <tr>
            <td colSpan="4">
              <i> Not operating in any ports </i>
            </td>
          </tr>
        )}
        {_.map(ports, (port) => (
          <tr key={port.id}>
            <td>{port.name}</td>
            <td>{port.countryName}</td>
            <td>{port.regionName}</td>
            <td>{port.biddingRegionsName}</td>
            {!readOnly && (
              <td>
                <TrashButton onClick={() => onRemove(port)} />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </Table>
  </div>
)

const PortsTable = ({ ports, selectedPorts, onTogglePort, onAddPorts }) => {
  const [filteredPorts, setFilteredPorts] = useState(ports)
  const portFilterOn = (p) => [p.name, p.countryName]

  const handleTogglePort = (port) => {
    onTogglePort(port)
    onAddPorts([port])
  }

  return (
    <div className="mb-4" {...uitest("add_additional_ports")}>
      <h3 className="h6">Add Additional Ports</h3>
      <div className="col-md-5 mt-3">
        <RegexSearchInput
          label="Search by port & country"
          items={ports}
          id="port_filter"
          onFiltered={setFilteredPorts}
          filterOn={portFilterOn}
        />
      </div>
      <Table>
        <thead>
          <tr>
            <th> </th>
            <th> Port </th>
            <th> Country </th>
            <th> Region </th>
            <th> CL Region </th>
          </tr>
        </thead>
        <tbody>
          {ports.length === 0 && (
            <tr>
              <td colSpan="4">
                <i> No Ports Found </i>
              </td>
            </tr>
          )}
          {_.map(filteredPorts, (port) => (
            <tr key={port.id}>
              <td>
                <Form.Check
                  id={port.id}
                  type="checkbox"
                  value={port.id}
                  defaultChecked={_.includes(_.map(selectedPorts, "id"), port.id)}
                  onChange={() => handleTogglePort(port)}
                />
              </td>
              <td>{port.name}</td>
              <td>{port.countryName}</td>
              <td>{port.regionName}</td>
              <td>{port.biddingRegionsName}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  )
}

const PortDetailsForm = (props): JSX.Element => {
  const dispatch = useDispatch()

  const { updatePortDetailsForm, updateOperatorDetails } = useSliceActions()

  const { submitting, operator, portDetailsForm, chats } = useSliceSelector(getForm)

  const ports = useSliceSelector(getPorts)

  const [currentPorts, currentPortFns] = useItemContainer(
    operator.operatingPorts,
    true,
    (updatedItems) => {
      dispatch(updateOperatorDetails({ values: { operatingPorts: updatedItems } }))
    }
  )

  const [selectedPorts, selectedPortsFns] = useItemContainer(
    portDetailsForm.selectedPorts,
    true,
    (updatedItems) => {
      dispatch(updatePortDetailsForm({ selectedPorts: updatedItems }))
    }
  )

  useEffect(() => {
    props.setValidity(currentPorts.length > 0)
  }, [currentPorts])

  const addPortsToCurrent = (ports) => {
    currentPortFns.addAll(ports)
    selectedPortsFns.removeAll(ports)
  }

  const portsForSelection = _.reject(ports, (p) => currentPortFns.includes(p))

  const chat = chats.find((chat) => chat.section === "port_details")

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center" }}>
        <h2 className="h5">{props.title}</h2>
        {chat && (
          <div
            style={{
              whiteSpace: "normal",
              overflowWrap: "break-word",
              wordBreak: "break-word",
            }}
            key={chat.id}
            className="mt-n2 ms-2"
          >
            <ChatButton chat={chat} />
          </div>
        )}
      </div>
      <p>Ports where your business currently has capabilities</p>

      <i>
        Note that this information will be updated globally (for all cruise lines) if edited here
      </i>
      <hr />
      <CurrentPortsTable
        ports={currentPorts}
        onRemove={currentPortFns.remove}
        readOnly={props.context.readOnly}
      />
      {!props.context.readOnly && (
        <PortsTable
          ports={portsForSelection}
          selectedPorts={selectedPorts}
          onTogglePort={selectedPortsFns.toggle}
          onAddPorts={addPortsToCurrent}
        />
      )}
      <div className="d-flex gap-2">{props.formNavigation}</div>
    </div>
  )
}

export default PortDetailsForm
